import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function OperationsTab() {
  const [health, setHealth] = useState(null);
  const [retryQueue, setRetryQueue] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  // Vendor control plane (6.5-A)
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorReadiness, setVendorReadiness] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hR, rR, tR, vR] = await Promise.all([
        api.get('/rcm/operations/health'),
        api.get('/rcm/operations/retry-queue'),
        api.get('/rcm/operations/timeline?hours=48&limit=20'),
        api.get('/rcm/integrations').catch(() => ({ data: { endpoints: [] } })),
      ]);
      setHealth(hR.data);
      setRetryQueue(rR.data);
      setTimeline(tR.data.events || []);
      setVendors(vR.data.endpoints || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadVendorReadiness = async (epId) => {
    setSelectedVendor(epId);
    try {
      const r = await api.get(`/rcm/integrations/${epId}/readiness`);
      setVendorReadiness(r.data);
    } catch { setVendorReadiness(null); }
  };

  const runHealthCheck = async (epId) => {
    try { await api.get(`/rcm/integrations/${epId}/health`); loadVendorReadiness(epId); }
    catch(e) { alert(e.response?.data?.detail || 'Health check failed'); }
  };

  const executeRetry = async (attemptId) => {
    try { await api.post(`/rcm/operations/retry/${attemptId}`); load(); }
    catch(e) { alert(e.response?.data?.detail || 'Retry failed'); }
  };

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading operations...</div>;

  const scoreColor = health?.health_score >= 80 ? 'text-emerald-600' : health?.health_score >= 50 ? 'text-amber-600' : 'text-red-600';
  const statusBg = health?.health_status === 'healthy' ? 'bg-emerald-50 border-emerald-200' : health?.health_status === 'degraded' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  return (
    <div className="space-y-4" data-testid="operations-tab">
      {/* Health Score */}
      <div className={`rounded-lg border p-4 flex items-center justify-between ${statusBg}`} data-testid="health-score">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Integration Health</p>
          <p className={`text-4xl font-bold ${scoreColor}`}>{health?.health_score || 0}<span className="text-lg text-muted-foreground">/100</span></p>
        </div>
        <div className="text-right">
          <Badge className={`text-sm ${health?.health_status === 'healthy' ? 'bg-emerald-100 text-emerald-700' : health?.health_status === 'degraded' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
            {health?.health_status?.toUpperCase()}
          </Badge>
          <p className="text-[10px] text-muted-foreground mt-1">{health?.generated_at?.slice(0,16).replace('T',' ')}</p>
        </div>
      </div>

      {/* ═══ PHASE 6.5-A: Vendor Control Plane ═══ */}
      {vendors.length > 0 && (
        <Card data-testid="vendor-control-plane">
          <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <Globe className="h-3 w-3" />Vendor Integrations ({vendors.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="space-y-2">
              {vendors.map((v) => {
                const envBg = v.environment === 'sandbox' ? 'bg-amber-50 border-amber-200' : v.environment === 'production' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200';
                const statusDot = v.status === 'healthy' ? 'bg-emerald-500' : v.status === 'configured' ? 'bg-amber-500' : 'bg-gray-400';
                return (
                  <div key={v.id} className={`border rounded p-2.5 text-xs cursor-pointer hover:ring-1 transition-all ${selectedVendor === v.id ? 'ring-2 ring-blue-400' : ''}`}
                       onClick={() => loadVendorReadiness(v.id)} data-testid={`vendor-${v.id?.slice(0,8)}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${statusDot}`} />
                        <span className="font-medium">{v.vendor_name || 'Unnamed'}</span>
                        <EnvironmentBadge env={v.environment} />
                        <Badge variant="outline" className="text-[9px]">{v.mode}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground text-[10px]">
                        <span>{v.credential_count || 0} creds</span>
                        <span>{v.status}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Readiness detail for selected vendor */}
            {vendorReadiness && (
              <div className="mt-3 border-t pt-3 space-y-2" data-testid="vendor-readiness">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium">Sandbox Readiness: {vendorReadiness.endpoint?.vendor_name}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => runHealthCheck(selectedVendor)}>
                      <RefreshCw className="h-2.5 w-2.5 mr-0.5" />Health Check
                    </Button>
                  </div>
                </div>
                <div className={`rounded p-2 ${vendorReadiness.ready ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2 text-xs">
                    {vendorReadiness.ready ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                    <span className="font-medium">{vendorReadiness.ready ? 'Sandbox Ready' : 'Not Ready'}</span>
                    <span className="text-muted-foreground">({vendorReadiness.passed_count}/{vendorReadiness.check_count} checks passed)</span>
                  </div>
                </div>
                <div className="space-y-0.5">
                  {vendorReadiness.checks?.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px]">
                      {c.passed ? <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500" /> : <XCircle className="h-2.5 w-2.5 text-red-500" />}
                      <span className="text-muted-foreground">{c.check.replace(/_/g, ' ')}</span>
                      <span className={c.passed ? '' : 'text-red-600'}>{c.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subsystem Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="subsystem-grid">
        {health?.subsystems && Object.entries(health.subsystems).map(([name, sub]) => {
          const bg = sub.status === 'healthy' ? 'border-emerald-200' : sub.status === 'degraded' ? 'border-amber-200' : 'border-red-200';
          const dot = sub.status === 'healthy' ? 'bg-emerald-500' : sub.status === 'degraded' ? 'bg-amber-500' : 'bg-red-500';
          return (
            <Card key={name} className={`${bg}`}>
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`h-2 w-2 rounded-full ${dot}`} />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{name.replace(/_/g,' ')}</span>
                </div>
                <div className="space-y-0.5 text-xs">
                  {Object.entries(sub).filter(([k]) => k !== 'status' && k !== 'latest_run').map(([k,v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-muted-foreground">{k.replace(/_/g,' ')}</span>
                      <span className="font-medium">{typeof v === 'number' ? (k.includes('rate') ? `${v}%` : v) : String(v)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Retry Queue */}
      {retryQueue && retryQueue.count > 0 && (
        <Card data-testid="retry-queue-panel">
          <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
            <CardTitle className="text-xs flex items-center gap-1.5">
              <RotateCw className="h-3 w-3" />Retry Queue ({retryQueue.count})
              {retryQueue.eligible_count > 0 && <Badge variant="outline" className="text-[9px]">{retryQueue.eligible_count} eligible</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 space-y-2">
            {retryQueue.items?.map((item) => {
              const elig = item.retry_evaluation || {};
              return (
                <div key={item.id} className="border rounded p-2 text-xs flex items-center justify-between" data-testid={`retry-item-${item.id?.slice(0,8)}`}>
                  <div>
                    <span className="font-medium">Attempt #{item.attempt_number}</span>
                    <TransportStatusChip status={item.transport_status} />
                    {item.external_reference_id && <span className="ml-2 font-mono text-muted-foreground">{item.external_reference_id}</span>}
                    <p className="text-muted-foreground mt-0.5">{elig.reason}</p>
                  </div>
                  {elig.eligible && (
                    <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => executeRetry(item.id)} data-testid={`retry-exec-${item.id?.slice(0,8)}`}>
                      <RotateCw className="h-2.5 w-2.5 mr-0.5" />Retry
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Operational Timeline */}
      <Card data-testid="ops-timeline">
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Operational Timeline (48h)</CardTitle>
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {timeline.length === 0 ? <p className="text-xs text-muted-foreground py-2 text-center">No recent events</p> : (
            <div className="space-y-1">
              {timeline.map((ev, i) => {
                const typeColors = { transport: 'text-blue-600', response: 'text-indigo-600', era_batch: 'text-teal-600', reconciliation: 'text-orange-600' };
                return (
                  <div key={i} className="flex items-start gap-2 text-xs py-1 border-b last:border-0">
                    <span className="text-[10px] text-muted-foreground w-28 shrink-0">{ev.timestamp?.slice(11,19)}</span>
                    <span className={`text-[10px] font-medium w-20 shrink-0 ${typeColors[ev.type] || 'text-gray-600'}`}>{ev.type}</span>
                    <StatusChip status={ev.status} size="xs" />
                    <span className="text-muted-foreground flex-1 truncate">{ev.detail}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
// FILTER STATE ENGINE (Phase 6A.1)
// Reusable filter context: persists, supports deep linking, multiple conditions
// ═══════════════════════════════════════
const FilterContext = React.createContext({ filters: {}, source: null, setFilters: () => {}, clearFilters: () => {} });

export { OperationsTab };
